import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { reduxForm, Field } from 'redux-form';
import { Button, Form, Grid, Message } from 'semantic-ui-react';
import { combineValidators, composeValidators, createValidator, isRequired } from 'revalidate';
import PropTypes from 'prop-types';
import { toastr } from 'react-redux-toastr';
import { confirmAlert } from 'react-confirm-alert';
import TextInput from '../../../app/common/Form/TextInput';
import PayWithDropDown from './PayWithDropDown';
import {
  addMoneyInit,
  applyPromoCode,
  applyFreeFundCode,
  resetPromoCode,
  resetFreeFundCode,
  toggleFreefundCodeForm,
  togglePromoCodeForm,
  validatePromoCode,
} from '../PaymentActions';
import { openModal } from '../../modals/modalActions';
import InPlacePromoCode from '../component/InPlacePromoCode';
import InPlaceFreeFundCode from '../component/InPlaceFreeFundCode';
import BalanceMessage from '../component/BalanceMessage';
import CustomNavLink from '../../nav/SideBar/component/CustomNavLink';
import { isMobile, isTablet } from 'react-device-detect';
import { ghostForm, resetReduxFormField, store } from '../../../app/common/util';

const mapState = state => ({
  auth: state.auth,
  async: state.async,
  addMoneyForm: state.form.addMoneyForm,
  cards: (state.cards && state.cards.fetchedCards && state.cards.fetchedCards.cards) || [],
  paymentState: state.payment,
  showFreefundCode: state.payment && state.payment.showFreefundCode,
  showPromoCode: state.payment && state.payment.showPromoCode,
});

const actions = {
  addMoneyInit,
  applyPromoCode,
  applyFreeFundCode,
  openModal,
  resetPromoCode,
  resetFreeFundCode,
  toggleFreefundCodeForm,
  togglePromoCodeForm,
  validatePromoCode
};

const isAmount = createValidator(
  message => value => {
    if (!value) return;
    if (!/^[0-9]\d*(\.\d+)?$/.test(value)) {
      return message;
    }
  },

  field => `${field} is required`
);

const isAmountGreaterThanZero = () => createValidator(
  message => value => {
    if (!value) return;
    if (value <= 0) {
      return `${message}`;
    }
  },

  field => `${field} is required`
);

const isAmountBelowLimit = () => createValidator(
  message => value => {
    const depositLimit = parseFloat(store.getState().payment.balance.depositLimit);
    if (!value) return;
    if (value > depositLimit) {
      return `${message}${depositLimit.toFixed(2)}`;
    }
  },

  field => `${field} is required`
);

const validate = combineValidators({
  txn_amount: composeValidators(
    isRequired({ message: "Amount is required" }),
    isAmount({ message: "Amount should be numeric" }),
    isAmountBelowLimit()({
      message: "Sorry! You can only add upto Rs. "
    }),
    isAmountGreaterThanZero()({
      message: "Amount should be greater than 0."
    })
  )()
});

class AddMoneyForm extends Component {
  static propTypes = {
    auth: PropTypes.object.isRequired,
    addMoneyInit: PropTypes.func.isRequired,
    openModal: PropTypes.func.isRequired,
    async: PropTypes.object.isRequired,
    applyPromoCode: PropTypes.func.isRequired,
    applyFreeFundCode: PropTypes.func.isRequired,
    addMoneyForm: PropTypes.object,
    cards: PropTypes.array,
    paymentState: PropTypes.object,
    resetPromoCode: PropTypes.func.isRequired,
    resetFreeFundCode: PropTypes.func.isRequired,
    showFreefundCode: PropTypes.bool,
    showPromoCode: PropTypes.bool,
    toggleFreefundCodeForm: PropTypes.func.isRequired,
    togglePromoCodeForm: PropTypes.func.isRequired,
    validatePromoCode: PropTypes.func.isRequired
  };

  constructor(props) {
    super(props);

    this.payload = {};
  }

  state = {
    showPromoCodeInput: false
  };

  openPromoCodeModal = () => {
    const { openModal } = this.props;
    openModal('PromoCodeModal');
  };

  appendPayload = object => {
    this.payload = Object.assign(this.payload, object);
  };

  submit = () => {
    const enableCheck = false;

    const { addMoneyInit, addMoneyForm, auth, paymentState } = this.props;
    this.appendPayload((addMoneyForm && addMoneyForm.values) || {});

    const { balance: { depositLimit}} = paymentState;

    const incoming = this.payload;
    let payload = {};
    const amountLimit = parseFloat(depositLimit);

    // Transaction amount is greater than deposit limit then throw error
    if (parseFloat(incoming['txn_amount']) > amountLimit) {
      toastr.error(
        'Add money service',
        'Amount exceeded deposit limit!'
      );

      return false;
    }

    if (incoming['card_cvv'] && !incoming['paymentMethod']) {
      incoming['paymentMethod'] = '1';
    }

    // quick validation for add money form
    if (Object.keys(incoming).length === 0 && incoming.constructor === Object) {
      toastr.error(
        'Add money service',
        'Please enter amount and choose payment method'
      );
      return false;
    } else if (!incoming['txn_amount']) {
      toastr.error('Add money service', 'Please enter amount');
      return false;
    } else if (!incoming['paymentMethod']) {
      toastr.error(
        'Add money service',
        'Please enter a CVV'
      );
      return false;
    }

    switch (incoming['paymentMethod']) {
      case '1':
        if (!incoming['card_num']) {
          toastr.error('Add money service', 'Please enter card number');
          return false;
        } else if (!incoming['card_exp_month']) {
          toastr.error(
            'Add money service',
            'Please enter expiry month for the card'
          );

          return false;
        } else if (!incoming['card_exp_year']) {
          toastr.error(
            'Add money service',
            'Please enter expiry year for the card'
          );
          return false;
        } else if (!incoming['card_cvv']) {
          toastr.error('Add money service', 'Please enter CVV for the card');
          return false;
        }
        break;
      case '2':
        if (!incoming['cvv']) {
          toastr.error('Add money service', 'Saved card CVV missing');
          return false;
        }
        else if (!incoming['cardToken']) {
          toastr.error('Add money service', 'Saved card token missing, please call support');
          return false;
        }
        break;
      case '3':
        if (!incoming['payment_option']) {
          toastr.error(
            'Add money service',
            'Please select bank from drop down'
          );
          return false;
        }
        break;
      default:
        break;
    }

    // KYC check
    if (
      enableCheck &&
      auth.isLoggedIn &&
      auth.isLoggedIn.kycWalletState === 'NO_KYC'
    ) {
      toastr.error(
        'KYC Error',
        'Please complete your KYC before adding money to your wallet'
      );

      return false;
    }

    if (
      enableCheck &&
      !parseInt(
        paymentState &&
        paymentState.balance &&
        paymentState.balance.depositLimit
      ) > 0
    ) {
      toastr.error(
        'Add Money',
        `You can't add money to your wallet, deposit limit is ${
        paymentState.balance.depositLimit
        }`
      );
      return false;
    }

    // Card payload
    payload = {
      txn_amount: incoming['txn_amount'],
      Source: 'FC',
      req_channel: 'WEB',
      payment_type: incoming['paymentMethod']
    };

    // IF Saved Card SELECTED
    if (payload.payment_type === '2') {
      payload['card_reference'] = incoming['card_reference'];
      payload['card_cvv'] = incoming['cvv'];
      payload['saved_card_token'] = incoming['cardToken'];
    }

    // IF Credit/Debit Card SELECTED
    if (payload.payment_type === '1') {
      payload['card_cvv'] = incoming['card_cvv'];
      payload['card_exp_month'] = incoming['card_exp_month'];
      payload['card_exp_year'] = incoming['card_exp_year'];
      payload['card_num'] = incoming['card_num'];
    }

    // IF Net Banking SELECTED
    if (payload.payment_type === '3') {
      payload['payment_option'] = incoming['payment_option'];
    }

    // IF Promocode is applied
    if (incoming['promo-code']) {
      this.validatePromocode(incoming, payload, (response, promoId) => {
        if (response && response.errorMessage === 'null') {
          // Request
          addMoneyInit(
            payload,
            auth.csrfRequestIdentifier,
            promoId,
            response => {
              // Forward to Payment Gateway
              var errorShown = false;
              if (
                response &&
                response.actionUrl &&
                response.actionUrl.length > 5
              ) {
                ghostForm(response.actionUrl, response.resultMap, ['pg_url']);
              } else if (
                response &&
                response.pg_url &&
                response.pg_url.length > 5
              ) {
                ghostForm(response.pg_url, response, ['pg_url']);
              } else if (response.errorMessage) {
                if (!errorShown)
                  toastr.error('Add Money', response.errorMessage);
                errorShown = true;
              } else {
                toastr.error(
                  'Add Money',
                  "Couldn't process the payment at the moment, please try after sometime."
                );
              }
            }
          );
        } else if (response && response.errorMessage) {
          toastr.error('Promocode validation', response.errorMessage);
        } else {
          toastr.error(
            'Promocode validation',
            'There was some problem while validating promocode'
          );
        }
      });
    } else {
      // Request
      addMoneyInit(payload, auth.csrfRequestIdentifier, '', response => {
        // Forward to Payment Gateway
        if (response && response.actionUrl && response.actionUrl.length > 5) {
          ghostForm(response.actionUrl, response.resultMap, ['pg_url']);
        } else if (response && response.pg_url && response.pg_url.length > 5) {
          ghostForm(response.pg_url, response, ['pg_url']);
        } else if (response.errorMessage) {
          toastr.error('Add Money', response.errorMessage);
        } else {
          toastr.error(
            'Add Money',
            "Couldn't process the payment at the moment, please try after sometime."
          );
        }
      }, auth.migration && auth.migration.walletToken);
    }
  };

  // Validate if the applied promoCode is applicable for the amount and type of payment
  validatePromocode = (incoming, payload, callback) => {
    const { auth, paymentState, validatePromoCode } = this.props;

    const promoId =
      paymentState && paymentState.promoCode && paymentState.promoCode.lookupId;

    if (promoId && typeof promoId === 'string')
      validatePromoCode(
        promoId,
        incoming.payment_option,
        incoming.payment_type,
        incoming.txn_amount,
        auth.csrfRequestIdentifier,
        response => {
          callback(response, promoId);
        }
      );
  };

  selectStoredCard = card => {
    this.appendPayload(card);
  };

  resetPromo = callback => {
    confirmAlert({
      title: 'Please confirm',
      message: `Are you sure, you want to remove promocode?`,
      buttons: [
        {
          label: 'Yes',
          onClick: () => {
            // Reset field
            resetReduxFormField('addMoneyForm', {
              'promo-code': '',
            });

            // Dispatch action
            this.props.resetPromoCode();

            callback();
          }
        },
        {
          label: 'No',
          onClick: () => { }
        }
      ]
    });
  };

  // Amount input onFocus handler
  amountFocused = () => {
    const { showFreefundCode, toggleFreefundCodeForm } = this.props;

    // Amount input onFocus AND freefundCode is visible THEN hide freefundCode form
    if (showFreefundCode) {
      toggleFreefundCodeForm();
    }
  }

  resetFreefund = callback => {
    // Reset field
    resetReduxFormField('addMoneyForm', {'freefund-code': ''}, this.props);

    // Dispatch action
    this.props.resetFreeFundCode();

    callback();
  };

  resetCreditCardField = () => {
    resetReduxFormField(
      'addMoneyForm',
      {
        'card_num': '',
        'card_exp_month': '',
        'card-vendor': '',
        'card_exp_year': '',
        'card_cvv': '',
      },
      this.props
    );
  }

  render() {
    const {
      addMoneyForm,
      applyFreeFundCode,
      applyPromoCode,
      auth,
      async,
      cards,
      handleSubmit,
      paymentState,
      showFreefundCode,
      showPromoCode,
      update,
      togglePromoCodeForm,
      toggleFreefundCodeForm,
    } = this.props;
    return (
      <Fragment>
        <Grid>
          <Grid.Row>
            <Grid.Column>
              <Form
                onSubmit={handleSubmit(this.submit)}
              >
                <div className="amountWrapper">
                  <Field
                    name="txn_amount"
                    maxLength={10}
                    placeholder="ENTER AMOUNT"
                    type="tel"
                    allowonlynumber="true"
                    component={TextInput}
                    onFocus={this.amountFocused}
                    autoComplete="off"
                  />

                  <div
                    className={`freeFund ${
                      isMobile || isTablet ? 'mobileandtab' : 'browser'
                      }`}
                  >
                    <Field
                      name="freefund-code"
                      placeholder="Enter Freefund Code"
                      type="text"
                      addMoneyForm={addMoneyForm}
                      auth={auth}
                      async={async}
                      applyFreeFundCode={applyFreeFundCode}
                      component={InPlaceFreeFundCode}
                      payment={paymentState}
                      resetFreefundCode={this.resetFreefund}
                      show={showFreefundCode}
                      toggle={toggleFreefundCodeForm}
                    />
                  </div>
                </div>

                <BalanceMessage payment={paymentState} />

                <PayWithDropDown
                  cards={cards}
                  selectStoredCard={this.selectStoredCard}
                  resetCreditCardField={this.resetCreditCardField.bind(this)}
                />
                <div className="clearfix">
                  <Field
                    name="promo-code"
                    placeholder="Enter Promocode"
                    type="text"
                    addMoneyForm={addMoneyForm}
                    auth={auth}
                    async={async}
                    applyPromoCode={applyPromoCode}
                    component={InPlacePromoCode}
                    payment={paymentState}
                    resetPromoCode={this.resetPromo}
                    show={showPromoCode}
                    toggle={togglePromoCodeForm}
                  />
                </div>
                <Message
                  success
                  header="Form Completed"
                  content="You're all signed up for the newsletter"
                />
                <Message error header="Form Error" content="Invalid input!" />

                <Button
                  as={CustomNavLink}
                  to="/accounts/"
                  className={`fcbtn fccancelbtn ${
                    isMobile || isTablet ? 'mobileandtab' : ''
                    }`}
                  onClick={update}
                >
                  Cancel
                </Button>
                <Button
                  className={`fcbtn fcprimary ${
                    isMobile || isTablet ? 'mobileandtab' : ''
                    }`}
                  type="submit"
                >
                  Proceed To Pay
                </Button>
              </Form>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Fragment>
    );
  }
}

export default connect(
  mapState,
  actions
)(
  reduxForm({ form: 'addMoneyForm', enableReinitialize: true, validate })(
    AddMoneyForm
  )
);
